/* The example dataset the proposal shows HydroHub with. One morning at Vestlandskraft (26 plants, 89,8 MW):
   Haugeland trips at 06.40, Vik has run reduced since last evening, two events have ended and wait for
   acknowledgement. Every view in the proposal – and the PDF – is drawn from these numbers, so they agree.
   All timestamps are UTC; Oslo is UTC+2 on 14 October (CEST). Fictional data. */

export const NOW = "2026-10-14T07:00:00Z"; // kl. 09.00 Oslo – the latest measurement
export const AS_OF_LABEL = "09.00";
export const CUSTOMER = { name: "Vestlandskraft Småkraft AS", short_name: "Vestlandskraft" };
export const USER = { name: "Kari Nordmann", title: "Driftssjef", email: "kari.nordmann@vestlandskraft.no", avatar_url: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=256&h=256&fit=crop&crop=faces&q=80" };

/* a tiny deterministic PRNG, so the curves are the same on every render and in the PDF */
const mulberry = (seed) => () => {
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};
const osloIso = (day, hour, minute = 0) => new Date(Date.UTC(2026, 9, day, hour - 2, minute)).toISOString();
const r1 = (v) => Math.round(v * 10) / 10;
const r2 = (v) => Math.round(v * 100) / 100;

/* ---------- plants ---------- */
const RAW = [
  ["Haugeland kraftverk", 2.8, "Ullensvang", "NO5"],
  ["Vik kraftverk", 1.8, "Kvam", "NO5"],
  ["Fjellbekken kraftverk", 1.6, "Sogndal", "NO5"],
  ["Myklebustelva kraftverk", 3.0, "Sykkylven", "NO3"],
  ["Nesdalen kraftverk", 3.7, "Vik", "NO5"],
  ["Ospeli kraftverk", 1.3, "Fjaler", "NO5"],
  ["Bjørndalen kraftverk", 7.9, "Høyanger", "NO5"],
  ["Botnaelva kraftverk", 0.8, "Kinn", "NO5"],
  ["Dalsfossen kraftverk", 4.4, "Stranda", "NO3"],
  ["Fossheim kraftverk", 3.2, "Luster", "NO5"],
  ["Gjerdeelva kraftverk", 2.2, "Sunnfjord", "NO5"],
  ["Grytdøla kraftverk", 2.1, "Luster", "NO5"],
  ["Storelvi kraftverk", 3.1, "Voss", "NO5"],
  ["Kvernhusfossen kraftverk", 5.6, "Gloppen", "NO5"],
  ["Skjerva kraftverk", 4.9, "Aurland", "NO5"],
  ["Tverrelva kraftverk", 6.2, "Stryn", "NO3"],
  ["Lonevåg kraftverk", 2.6, "Osterøy", "NO5"],
  ["Rysjedalsvika kraftverk", 3.4, "Hyllestad", "NO5"],
  ["Hestadfossen kraftverk", 7.2, "Sunnfjord", "NO5"],
  ["Åsåna kraftverk", 1.9, "Masfjorden", "NO5"],
  ["Jostedøla kraftverk", 4.1, "Luster", "NO5"],
  ["Skarvefossen kraftverk", 5.8, "Lærdal", "NO5"],
  ["Movatn kraftverk", 2.4, "Bremanger", "NO5"],
  ["Eikefet kraftverk", 3.0, "Alver", "NO5"],
  ["Ulvundelva kraftverk", 2.3, "Vaksdal", "NO5"],
  ["Breidalen kraftverk", 2.5, "Ullensvang", "NO5"],
];
const rndPlant = mulberry(14);
export const PLANTS = RAW.map(([name, capacity_mw, municipality, price_area], i) => {
  const id = name.toLowerCase().replace(/ kraftverk$/, "").replace(/[^a-zæøå]/g, "");
  const util = 0.87 + 0.13 * rndPlant();
  let status = "running";
  let power = capacity_mw * util;
  let status_reason = null;
  let events_open = 0;
  let events_critical = 0;
  let events_ongoing = 0;
  let events_unacknowledged = 0;
  if (id === "haugeland") {
    status = "stopped";
    power = 0;
    status_reason = "Utfall – overstrøm generator";
    events_open = events_critical = events_ongoing = events_unacknowledged = 1;
  } else if (id === "vik") {
    status = "reduced";
    power = 0.72;
    status_reason = "Redusert drift – delvis tett inntaksrist";
    events_open = events_ongoing = 1;
  } else if (id === "fjellbekken" || id === "myklebustelva") {
    events_open = events_unacknowledged = 1;
  } else if (id === "nesdalen") {
    events_open = events_critical = 1;
  }
  const energy_today = status === "stopped" ? 12.9 : status === "reduced" ? 9.4 : power * 10 * (0.97 + 0.03 * rndPlant());
  return {
    id,
    name,
    capacity_mw,
    municipality,
    price_area,
    status,
    status_reason,
    power_mw: r2(power),
    utilisation_pct: Math.round((100 * power) / capacity_mw),
    energy_today_mwh: r1(energy_today),
    events_open,
    events_critical,
    events_ongoing,
    events_unacknowledged,
    measured_at: NOW,
  };
});
/* the portfolio adds up to the figure in the head: 78,9 MW of 89,8 */
(() => {
  const target = 78.9;
  const sum = PLANTS.reduce((s, p) => s + p.power_mw, 0);
  const bj = PLANTS.find((p) => p.name.startsWith("Bjørndalen"));
  bj.power_mw = r2(bj.power_mw + (target - sum));
  bj.utilisation_pct = Math.round((100 * bj.power_mw) / bj.capacity_mw);
})();
export const CAPACITY_MW = r1(RAW.reduce((s, x) => s + x[1], 0)); // 89,8
export const POWER_NOW_MW = 78.9;

export const PORTFOLIO = {
  plants_total: PLANTS.length,
  plants_running: 25,
  capacity_mw: CAPACITY_MW,
  power_now_mw: POWER_NOW_MW,
  utilisation_pct: Math.round((100 * POWER_NOW_MW) / CAPACITY_MW),
  energy_today_mwh: 788,
  energy_yesterday_same_time_mwh: 806,
  energy_today_delta_pct: -2.2,
  revenue_today_nok: 465000,
  spot_now_nok_mwh: 602,
  availability_30d_pct: 99.6,
  energy_30d_mwh: 48960,
};

/* ---------- today vs yesterday, portfolio (MW per hour) ---------- */
const TODAY_MW = [77.4, 77.1, 77.6, 78.3, 79.0, 80.6, 81.2, 79.1, 78.8, 78.9];
const YESTERDAY_MW = [80.2, 79.8, 79.5, 79.9, 80.4, 81.0, 81.6, 81.9, 81.4, 80.1, 80.3, 80.6, 80.1, 79.8, 79.6, 79.9, 80.2, 80.5, 79.0, 78.6, 78.9, 79.1, 78.7, 78.3];
export const SERIES_TODAY = TODAY_MW.map((p, h) => ({ ts: osloIso(14, h), power_mw: p }));
export const SERIES_YESTERDAY = YESTERDAY_MW.map((p, h) => ({ ts: osloIso(13, h), power_mw: p }));

/* ---------- what needs attention ---------- */
export const FOLLOW_UP = [
  { id: "haugeland", name: "Haugeland", word: "Stanset", tone: "stopped", reason: "Utfall – overstrøm generator", meta: "Siden kl. 06.40 · ikke kvittert · 0,00 av 2,8 MW", link: true },
  { id: "vik", name: "Vik", word: "Redusert", tone: "reduced", reason: "Redusert drift – delvis tett inntaksrist", meta: "Siden 13. okt. kl. 18.00 · kvittert · 0,72 av 1,8 MW", link: true },
  { id: "fjellbekken", name: "Fjellbekken", word: "I drift igjen", tone: "muted", reason: "Redusert drift – manuell begrensning etter inspeksjon", meta: "Avsluttet kl. 07.50 · ikke kvittert", link: true },
  { id: "myklebustelva", name: "Myklebustelva", word: "I drift igjen", tone: "muted", reason: "Dørkontakt åpnet – kraftstasjon", meta: "Avsluttet kl. 00.59 · ikke kvittert", link: true },
];

/* ---------- events (Hendelser) ---------- */
export const EVENTS = [
  { id: "ev-haug-1410", severity: "critical", title: "Utfall – overstrøm generator", kind: "trip", kind_label: "Utfall", plant_name: "Haugeland kraftverk", started_at: osloIso(14, 6, 40), ended_at: null, duration_h: 2.33, status: "open", production_stop: true, rel: "2 timer siden" },
  { id: "ev-vik-1310", severity: "warning", title: "Redusert drift – delvis tett inntaksrist", kind: "derate", kind_label: "Redusert drift", plant_name: "Vik kraftverk", started_at: osloIso(13, 18, 0), ended_at: null, duration_h: 15, status: "acknowledged", production_stop: false, rel: "15 timer siden" },
  { id: "ev-fjell-1410", severity: "warning", title: "Redusert drift – manuell begrensning etter inspeksjon", kind: "derate", kind_label: "Redusert drift", plant_name: "Fjellbekken kraftverk", started_at: osloIso(14, 5, 10), ended_at: osloIso(14, 7, 50), duration_h: 2.67, status: "open", production_stop: false, rel: "4 timer siden" },
  { id: "ev-mykle-1410", severity: "info", title: "Dørkontakt åpnet – kraftstasjon", kind: "alarm", kind_label: "Alarm", plant_name: "Myklebustelva kraftverk", started_at: osloIso(14, 0, 2), ended_at: osloIso(14, 0, 59), duration_h: 0.95, status: "open", production_stop: false, rel: "9 timer siden" },
  { id: "ev-nes-1310", severity: "critical", title: "Utfall – feil på hydraulikkaggregat", kind: "trip", kind_label: "Utfall", plant_name: "Nesdalen kraftverk", started_at: osloIso(13, 9, 28), ended_at: osloIso(13, 13, 9), duration_h: 3.68, status: "acknowledged", production_stop: true, rel: "i går" },
  { id: "ev-stor-1210", severity: "info", title: "Reservekraft testkjørt OK", kind: "alarm", kind_label: "Alarm", plant_name: "Storelvi kraftverk", started_at: osloIso(12, 10, 5), ended_at: osloIso(12, 12, 41), duration_h: 2.6, status: "acknowledged", production_stop: false, rel: "2 dager siden" },
  { id: "ev-haug-1010", severity: "warning", title: "Høy vannstand i inntak", kind: "alarm", kind_label: "Alarm", plant_name: "Haugeland kraftverk", started_at: osloIso(10, 16, 31), ended_at: osloIso(10, 18, 25), duration_h: 1.9, status: "resolved", production_stop: false, rel: "4 dager siden" },
  { id: "ev-tver-0910", severity: "critical", title: "Utfall – nettbortfall NO3", kind: "trip", kind_label: "Utfall", plant_name: "Tverrelva kraftverk", started_at: osloIso(9, 22, 14), ended_at: osloIso(9, 23, 2), duration_h: 0.8, status: "resolved", production_stop: true, rel: "5 dager siden" },
  { id: "ev-kver-0810", severity: "info", title: "Planlagt vedlikehold – rensk av varegrind", kind: "maintenance", kind_label: "Vedlikehold", plant_name: "Kvernhusfossen kraftverk", started_at: osloIso(8, 8, 0), ended_at: osloIso(8, 13, 30), duration_h: 5.5, status: "resolved", production_stop: true, rel: "6 dager siden" },
  { id: "ev-bjor-0610", severity: "warning", title: "Høy lagertemperatur generator", kind: "alarm", kind_label: "Alarm", plant_name: "Bjørndalen kraftverk", started_at: osloIso(6, 14, 48), ended_at: osloIso(6, 16, 10), duration_h: 1.4, status: "resolved", production_stop: false, rel: "8 dager siden" },
  { id: "ev-dals-0410", severity: "critical", title: "Utfall – lavt oljetrykk regulator", kind: "trip", kind_label: "Utfall", plant_name: "Dalsfossen kraftverk", started_at: osloIso(4, 3, 22), ended_at: osloIso(4, 9, 45), duration_h: 6.4, status: "resolved", production_stop: true, rel: "10 dager siden" },
  { id: "ev-skje-0310", severity: "info", title: "Kommunikasjonsbrudd mot driftssentral", kind: "alarm", kind_label: "Alarm", plant_name: "Skjerva kraftverk", started_at: osloIso(3, 11, 7), ended_at: osloIso(3, 11, 29), duration_h: 0.37, status: "resolved", production_stop: false, rel: "11 dager siden" },
];
export const EVENT_COUNTS = { open: 3, critical_open: 2, last_30d: 41, stops_30d: 17 };

/* ---------- Haugeland: the week, hour by hour ---------- */
export const HAUGELAND = {
  id: "haugeland",
  name: "Haugeland kraftverk",
  municipality: "Ullensvang",
  county: "Vestland",
  price_area: "NO5",
  capacity_mw: 2.8,
  status: "stopped",
  status_reason: "Utfall – overstrøm generator",
  stopped_since: osloIso(14, 6, 40),
  power_mw: 0,
  utilisation_pct: 0,
  flow_m3s: 1.04,
  design_flow_m3s: 1.18,
  turbine_flow_m3s: 0,
  level_m: 470.59,
  intake_level_min_m: 469.0,
  intake_level_max_m: 470.6,
  spot_nok_mwh: 614,
  energy_today_mwh: 12.9,
  revenue_today_nok: 7920,
  head_m: 285,
  turbine_type: "Pelton",
  units: 1,
  commissioned_year: 2006,
  annual_estimate_gwh: 11.2,
  kpis: { energy_today_mwh: 12.9, energy_today_delta_pct: -52.4, energy_30d_mwh: 1568, energy_30d_delta_pct: 29.7, availability_30d_pct: 98.2, hours_stopped_30d: 13, capacity_factor_30d_pct: 78, revenue_30d_nok: 819297, avg_spot_30d_nok_mwh: 519 },
};

const weekSeries = () => {
  const r = mulberry(7);
  const rows = [];
  const start = Date.UTC(2026, 9, 7, 7); // 7. okt kl. 09.00
  const stop = Date.UTC(2026, 9, 14, 4, 40);
  let walk = 0;
  for (let i = 0; i <= 168; i++) {
    const ts = start + i * 3600_000;
    const day = new Date(ts).getUTCDate();
    const hour = (new Date(ts).getUTCHours() + 2) % 24;
    walk = Math.max(-0.05, Math.min(0.04, walk + (r() - 0.5) * 0.016));
    /* inflow: full after rain on the 7th–8th, then a dry week – the river falls a little every day,
       with a small daily swing (snowmelt peaks in the afternoon) */
    const base = { 7: 1.0, 8: 0.995, 9: 0.975, 10: 0.955, 11: 0.935, 12: 0.915, 13: 0.89, 14: 0.875 }[day] ?? 0.9;
    const daily = 1 - 0.02 * Math.cos(((hour - 16) / 24) * Math.PI * 2);
    const inflow = base * daily + walk;
    let power = Math.min(2.8, 2.86 * inflow + (r() - 0.5) * 0.03);
    let status = "running";
    if (ts >= stop) {
      power = 0;
      status = "stopped";
    } else if (ts + 3600_000 > stop) {
      power *= (stop - ts) / 3600_000; // the hour the trip happened
    }
    const flow = 1.18 * inflow * (0.99 + r() * 0.02);
    rows.push({ ts: new Date(ts).toISOString(), power_mw: r2(Math.max(0, power)), flow_m3s: r2(flow), level_m: r2(470.1 + (r() - 0.5) * 0.08 + (status === "stopped" ? 0.45 : 0)), spot_nok_mwh: Math.round(560 + 90 * Math.sin((i / 24) * Math.PI * 2 + 1) + (r() - 0.5) * 30), status });
  }
  return rows;
};
export const HAUGELAND_WEEK = weekSeries();
export const HAUGELAND_EVENTS = [
  { id: "ev-haug-1410", started_at: osloIso(14, 6, 40), ended_at: null, production_stop: true, kind: "trip", severity: "critical", title: "Utfall – overstrøm generator" },
  { id: "ev-haug-1010", started_at: osloIso(10, 16, 31), ended_at: osloIso(10, 18, 25), production_stop: false, kind: "alarm", severity: "warning", title: "Høy vannstand i inntak" },
];
const weekEnergy = HAUGELAND_WEEK.reduce((s, x) => s + x.power_mw, 0);
export const HAUGELAND_SUMMARY = {
  energy_mwh: Math.round(weekEnergy),
  avg_power_mw: r2(weekEnergy / HAUGELAND_WEEK.length),
  max_power_mw: Math.max(...HAUGELAND_WEEK.map((x) => x.power_mw)),
  capacity_factor_pct: Math.round((100 * weekEnergy) / (2.8 * HAUGELAND_WEEK.length)),
  hours_stopped: 2.3,
  revenue_nok: Math.round(HAUGELAND_WEEK.reduce((s, x) => s + x.power_mw * x.spot_nok_mwh, 0)),
};
export const HAUGELAND_EVENT_ROWS = [
  { id: "ev-haug-1410", severity: "critical", title: "Utfall – overstrøm generator", kind_label: "Utfall", started_at: osloIso(14, 6, 40), ended_at: null, duration_h: 2.33, status: "open", production_stop: true },
  { id: "ev-haug-1010", severity: "warning", title: "Høy vannstand i inntak", kind_label: "Alarm", started_at: osloIso(10, 16, 31), ended_at: osloIso(10, 18, 25), duration_h: 1.9, status: "resolved", production_stop: false },
  { id: "ev-haug-0310", severity: "info", title: "Reservekraft testkjørt OK", kind_label: "Alarm", started_at: osloIso(3, 10, 5), ended_at: osloIso(3, 12, 41), duration_h: 2.6, status: "resolved", production_stop: false },
  { id: "ev-haug-0909", severity: "critical", title: "Utfall – overstrøm generator", kind_label: "Utfall", started_at: "2026-09-09T19:37:00Z", ended_at: "2026-09-10T05:13:00Z", duration_h: 9.6, status: "resolved", production_stop: true },
  { id: "ev-haug-2708", severity: "info", title: "Planlagt vedlikehold – årskontroll", kind_label: "Vedlikehold", started_at: "2026-08-27T07:00:00Z", ended_at: "2026-08-29T14:00:00Z", duration_h: 55, status: "resolved", production_stop: true },
];
export const HAUGELAND_SERVICE = { type: "Inspeksjon inntak", date: "2026-04-12", technician: "T. Solheim", report_ref: "SR-2024-827", description: "Visuell inspeksjon og rensk av inntak, varegrind og luke." };

/* ---------- Rapporter: September 2026 ---------- */
const rndRep = mulberry(9);
export const REPORT = {
  period_label: "1.–30. september 2026",
  energy_mwh: 48960,
  revenue_nok: 29400000,
  avg_spot_nok_mwh: 600,
  capacity_factor_pct: 75,
  hours_stopped: 118,
  availability_pct: 99.4,
  per_day: Array.from({ length: 30 }, (_, i) => ({ date: new Date(Date.UTC(2026, 8, i + 1, 10)).toISOString(), energy_mwh: Math.round(1480 + 380 * Math.sin((i / 30) * Math.PI * 2 - 1.2) + (rndRep() - 0.5) * 160) })),
  rows: [
    ["Hestadfossen kraftverk", 7.2, 4130, 80, 100.0, 0, 598, 2470000],
    ["Bjørndalen kraftverk", 7.9, 4020, 71, 98.9, 8, 603, 2424000],
    ["Tverrelva kraftverk", 6.2, 3560, 80, 100.0, 0, 571, 2033000],
    ["Skarvefossen kraftverk", 5.8, 3180, 76, 99.6, 3, 605, 1924000],
    ["Kvernhusfossen kraftverk", 5.6, 2960, 73, 99.2, 6, 599, 1773000],
    ["Skjerva kraftverk", 4.9, 2710, 77, 100.0, 0, 607, 1645000],
    ["Dalsfossen kraftverk", 4.4, 2380, 75, 98.1, 14, 574, 1366000],
    ["Jostedøla kraftverk", 4.1, 2210, 75, 99.4, 4, 602, 1330000],
    ["Nesdalen kraftverk", 3.7, 1910, 72, 97.5, 18, 604, 1154000],
    ["Haugeland kraftverk", 2.8, 1480, 73, 98.3, 12, 596, 882000],
  ].map(([name, capacity_mw, energy_mwh, cf, availability_pct, hours_stopped, avg_price_nok_mwh, revenue_nok]) => ({ name, capacity_mw, energy_mwh, capacity_factor_pct: cf, availability_pct, hours_stopped, avg_price_nok_mwh, revenue_nok })),
};

/* ---------- Service ---------- */
export const SERVICE_REQUESTS = [
  { id: "sr-1", ref: "H-2026-0142", subject: "Feilsøking generatorvern etter utfall", status: "mottatt", category_label: "Feilretting", plant_name: "Haugeland kraftverk", when: "for 1 time siden", by: "Kari Nordmann" },
  { id: "sr-2", ref: "H-2026-0139", subject: "Rensk av inntaksrist", status: "planlagt", category_label: "Vedlikehold", plant_name: "Vik kraftverk", when: "i går", by: "Ola Hansen", sub: "Planlagt torsdag 16. okt." },
  { id: "sr-3", ref: "H-2026-0133", subject: "Årskontroll og lagermåling", status: "pagar", category_label: "Planlagt vedlikehold", plant_name: "Bjørndalen kraftverk", when: "for 6 dager siden", by: "Kari Nordmann" },
  { id: "sr-4", ref: "H-2026-0127", subject: "Kalibrering av vannstandsmåler", status: "fullfort", category_label: "Instrumentering", plant_name: "Skjerva kraftverk", when: "for 2 uker siden", by: "Ola Hansen" },
];
export const SERVICE_RECORDS = [
  { id: "rec-1", date: "2026-10-02", plant: "Skjerva kraftverk", type: "Instrumentering", description: "Kalibrering av vannstandsmåler i inntak, kontroll mot manuell peiling.", technician: "M. Aase", hours: 3.5, report_ref: "SR-2026-311" },
  { id: "rec-2", date: "2026-09-18", plant: "Hestadfossen kraftverk", type: "Vedlikehold", description: "Skifte av pakninger på ledeapparat, smøring av lagre.", technician: "T. Solheim", hours: 7, report_ref: "SR-2026-298" },
  { id: "rec-3", date: "2026-08-29", plant: "Haugeland kraftverk", type: "Årskontroll", description: "Årskontroll med vibrasjonsmåling og kontroll av vern. Ingen avvik.", technician: "T. Solheim", hours: 12, report_ref: "SR-2026-274" },
  { id: "rec-4", date: "2026-07-11", plant: "Nesdalen kraftverk", type: "Feilretting", description: "Utskifting av trykkgiver på hydraulikkaggregat.", technician: "R. Nygård", hours: 4, report_ref: "SR-2026-240" },
  { id: "rec-5", date: "2026-06-05", plant: "Vik kraftverk", type: "Inspeksjon", description: "Inspeksjon av inntak og varegrind etter flom.", technician: "M. Aase", hours: 2.5, report_ref: "SR-2026-212" },
];

/* ---------- Spør HydroHub: the question and the answer ---------- */
const last24 = HAUGELAND_WEEK.slice(-25).map((x) => ({ t: x.ts, effekt: x.power_mw, vannforing: x.flow_m3s }));
export const AI_QUESTION = "Hvorfor stanset Haugeland i dag?";
export const AI_MESSAGES = [
  { id: "u1", role: "user", content: AI_QUESTION },
  {
    id: "a1",
    role: "assistant",
    tools: [
      { call_id: "t1", name: "get_timeseries", label: "Effekt og vannføring siste 24 timer", done: true, ok: true },
      { call_id: "t2", name: "get_events", label: "Hendelser ved Haugeland", done: true, ok: true },
      { call_id: "t3", name: "get_service", label: "Servicehistorikk", done: true, ok: true },
    ],
    artifacts: [
      {
        id: "art1",
        type: "chart",
        title: "Haugeland – effekt og vannføring siste 24 timer",
        subtitle: "13. okt. kl. 09.00 – 14. okt. kl. 09.00 · timeverdier",
        source: "Hydroscada (simulert) · timeverdier",
        chart: { kind: "line", x_key: "t", x_type: "time", resolution: "hour", series: [{ key: "effekt", label: "Effekt", unit: "MW" }, { key: "vannforing", label: "Vannføring", unit: "m³/s", axis: "right" }], data: last24 },
      },
    ],
    content:
      "**Dokumentert i dataene**\n\nHaugeland produserte 2,5 MW frem til kl. 06.40, da effekten falt til 0 innenfor samme time. Hendelsesloggen har en vernutløsning kl. 06.40: *Utfall – overstrøm generator*, kritisk, fortsatt åpen og ikke kvittert. Vannføringen i elven var stabil på 1,02–1,05 m³/s før og etter stansen, så fallet skyldes ikke tilsig.\n\n**Mulige forklaringer**\n\nOverstrømsvern løser typisk ut ved feil i generator eller på nettsiden. Siste årskontroll (29. august) fant ingen avvik på vernet. Et tilsvarende utfall 9. september varte 9,6 timer. Proximas driftssentral er varslet; forløpet står i hendelsen.\n\nTapt produksjon så langt i dag: ca. **5,8 MWh** sammenlignet med samme timer i går.",
  },
];
export const AI_HISTORY = [
  { group: "I dag", items: [{ title: "Hvorfor stanset Haugeland i dag?", active: true }, { title: "Produksjon september per kraftverk" }] },
  { group: "Denne uken", items: [{ title: "Sammenlign Vik med forrige måned" }, { title: "Hendelser med produksjonsstans siste 30 dager" }, { title: "Hvordan ligger Bjørndalen an mot årsestimatet?" }] },
];
